/**
 * Tákn: bottom-sheet palette of music symbols (ChoirMate-style rows: accidentals, digits, dynamics,
 * articulations, fermata & bowings, notes, rests, ornaments & octaves, segno/coda, other), with the
 * recently used row on top, colour swatches and a size slider. OK arms the stamp tool; then each
 * tap on the page places the symbol centred on the tap.
 */
import { useEffect, useMemo, useState } from 'react';
import { PEN_COLORS, STAMP_SIZE_RANGE } from '../../shared/annotations';
import { SMUFL_BY_NAME, SMUFL_GLYPHS, SMUFL_GROUP_ORDER, type SmuflGlyph, type SmuflGroup } from '../../shared/smufl';
import { Button, cx, Sheet } from '../components/ui';
import { useI18n, useT, type MessageKey } from '../i18n';
import { GlyphIcon } from './GlyphIcon';
import { ColorSwatches, SizeSlider } from './ToolOptions';

const GROUP_LABEL: Record<SmuflGroup, MessageKey> = {
  accidentals: 'annotate.group_accidentals',
  digits: 'annotate.group_digits',
  dynamics: 'annotate.group_dynamics',
  articulations: 'annotate.group_articulations',
  technique: 'annotate.group_technique',
  notes: 'annotate.group_notes',
  rests: 'annotate.group_rests',
  ornaments: 'annotate.group_ornaments',
  navigation: 'annotate.group_navigation',
  other: 'annotate.group_other',
};

function GlyphButton({ glyph, selected, color, onSelect, onConfirm }: { glyph: SmuflGlyph; selected: boolean; color: string; onSelect: () => void; onConfirm: () => void }) {
  const { lang } = useI18n();
  const label = lang === 'is' ? glyph.is : glyph.en;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      title={label}
      onClick={onSelect}
      onDoubleClick={onConfirm}
      className={cx(
        'flex size-16 items-center justify-center rounded-2xl border bg-white transition',
        selected ? 'border-transparent ring-[3px] ring-[var(--accent)]' : 'border-[var(--line)] hover:border-[var(--accent)]',
      )}
    >
      <GlyphIcon glyph={glyph} size={52} color={selected ? color : '#111111'} />
    </button>
  );
}

export function SymbolPalette({
  open,
  initial,
  recent,
  onConfirm,
  onClose,
}: {
  open: boolean;
  initial: { glyph: string | null; color: string; size: number };
  recent: readonly string[];
  onConfirm: (glyph: string, color: string, size: number) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [glyph, setGlyph] = useState<string | null>(initial.glyph);
  const [color, setColor] = useState(initial.color);
  const [size, setSize] = useState(initial.size);

  // Each time the palette opens it starts from the current stamp settings.
  useEffect(() => {
    if (!open) return;
    setGlyph(initial.glyph);
    setColor(initial.color);
    setSize(initial.size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const groups = useMemo(() => SMUFL_GROUP_ORDER.map((g) => ({ group: g, glyphs: SMUFL_GLYPHS.filter((x) => x.group === g) })).filter((g) => g.glyphs.length > 0), []);
  const recentGlyphs = recent.map((n) => SMUFL_BY_NAME.get(n)).filter((g): g is SmuflGlyph => !!g);
  const current = glyph ? SMUFL_BY_NAME.get(glyph) : undefined;
  const confirm = (name: string | null) => {
    if (name) onConfirm(name, color, size);
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      wide
      title={t('annotate.palette_title')}
      footer={
        <>
          <Button variant="secondary" block onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button block disabled={!glyph} onClick={() => confirm(glyph)}>
            {t('common.ok')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl bg-[var(--surface-2)] p-2">
          <div
            className="flex size-16 shrink-0 items-center justify-center rounded-xl border border-[var(--line)] bg-white"
            aria-label={t('annotate.palette_preview')}
            role="img"
          >
            {current ? <GlyphIcon glyph={current} size={30 + ((size - STAMP_SIZE_RANGE[0]) / (STAMP_SIZE_RANGE[1] - STAMP_SIZE_RANGE[0])) * 30} color={color} /> : null}
          </div>
          <ColorSwatches colors={PEN_COLORS} value={color} onChange={setColor} />
          <SizeSlider value={size} min={STAMP_SIZE_RANGE[0]} max={STAMP_SIZE_RANGE[1]} onChange={setSize} />
        </div>

        {recentGlyphs.length > 0 ? (
          <section>
            <h3 className="mb-2 text-[15px] font-bold text-[var(--muted)]">{t('annotate.palette_recent')}</h3>
            <div role="radiogroup" aria-label={t('annotate.palette_recent')} className="flex flex-wrap gap-2">
              {recentGlyphs.map((g) => (
                <GlyphButton
                  key={g.name}
                  glyph={g}
                  selected={glyph === g.name}
                  color={color}
                  onSelect={() => setGlyph(g.name)}
                  onConfirm={() => confirm(g.name)}
                />
              ))}
            </div>
          </section>
        ) : null}

        {groups.map(({ group, glyphs }) => (
          <section key={group}>
            <h3 className="mb-2 text-[15px] font-bold text-[var(--muted)]">{t(GROUP_LABEL[group])}</h3>
            <div role="radiogroup" aria-label={t(GROUP_LABEL[group])} className="flex flex-wrap gap-2">
              {glyphs.map((g) => (
                <GlyphButton key={g.name} glyph={g} selected={glyph === g.name} color={color} onSelect={() => setGlyph(g.name)} onConfirm={() => confirm(g.name)} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </Sheet>
  );
}
